// frontend/src/components/TaxDraftsSection.jsx
// Borradores AEAT informativos (roadmap 2.5): modelos 303 y 130 en pantalla
// (tabla casilla → importe → de dónde sale) + descarga en PDF con marca de agua.
// El disclaimer es innegociable y está SIEMPRE visible: tu gestoría presenta.
//
// Patrón responsive: cabeceras con
// flex-wrap, tabla envuelta en overflow-x-auto propio y sin anchos fijos.
import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, Landmark, RefreshCw } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

const SECCIONES = {
  devengado: "IVA devengado",
  deducible: "IVA deducible",
  resultado: "Resultado",
  rendimiento: "Rendimiento de la actividad (acumulado del año)",
  pago: "Pago fraccionado",
  total: "Total de la autoliquidación",
};

function CasillasTable({ casillas, testid }) {
  let seccion = null;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-300 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 font-medium">Casilla</th>
            <th className="py-2 pr-3 font-medium">Concepto</th>
            <th className="py-2 pr-3 font-medium text-right">Importe</th>
            <th className="py-2 font-medium">De dónde sale</th>
          </tr>
        </thead>
        <tbody>
          {casillas.map((fila, i) => {
            const rows = [];
            if (fila.seccion !== seccion) {
              seccion = fila.seccion;
              rows.push(
                <tr key={`sec-${i}`} className="bg-slate-50">
                  <td colSpan={4} className="py-1.5 px-2 text-xs font-semibold text-slate-600">
                    {SECCIONES[seccion] || seccion}
                  </td>
                </tr>
              );
            }
            rows.push(
              <tr key={i} className={`border-b border-slate-100 ${fila.es_total ? "bg-slate-50/50" : ""}`}>
                <td className="py-1.5 pr-3 font-mono text-slate-700">{fila.num}</td>
                <td className={`py-1.5 pr-3 ${fila.es_total ? "font-semibold" : ""}`}>{fila.concepto}</td>
                <td className={`py-1.5 pr-3 text-right font-mono whitespace-nowrap ${fila.es_total ? "font-semibold" : ""}`}>
                  {fila.unidad === "pct" ? `${fila.importe} %` : fmtEur(fila.importe)}
                </td>
                <td className="py-1.5 text-xs text-slate-500">{fila.origen}</td>
              </tr>
            );
            return rows;
          })}
        </tbody>
      </table>
    </div>
  );
}

function NotasYFuentes({ data }) {
  return (
    <div className="space-y-2">
      {data.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-1">
          {data.warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
          ))}
        </div>
      )}
      {data.notas?.length > 0 && (
        <ul className="space-y-1">
          {data.notas.map((n, i) => (
            <li key={i} className="text-xs text-slate-500">• {n}</li>
          ))}
        </ul>
      )}
      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer select-none hover:text-slate-700">
          Fuentes por casilla (instrucciones oficiales)
        </summary>
        <ul className="mt-1 space-y-0.5 pl-4">
          {Object.entries(
            data.casillas.reduce((acc, f) => {
              (acc[f.fuente] = acc[f.fuente] || []).push(f.num);
              return acc;
            }, {})
          ).map(([fuente, nums]) => (
            <li key={fuente}>
              Casilla(s) {nums.join(", ")}: {fuente}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

// `request` (opcional): {year, quarter, ts} — lo manda el enlace del cierre de
// trimestre para abrir el borrador de ese periodo (bloque 4 del 2.5).
export default function TaxDraftsSection({ request }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [basis, setBasis] = useState("dates");
  const [loading, setLoading] = useState(false);
  const [data303, setData303] = useState(null);
  const [err303, setErr303] = useState(null);
  const [data130, setData130] = useState(null);
  const [show130, setShow130] = useState(false);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const sectionRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r303 = await apiFetch(
        `${API}/tax-drafts/modelo-303?year=${year}&quarter=${quarter}&basis=${basis}`
      );
      if (r303.ok) {
        setData303(await r303.json());
        setErr303(null);
      } else {
        const e = await r303.json().catch(() => ({}));
        setErr303(e.detail || "No se pudo generar el borrador del 303");
        setData303(null);
      }
      const r130 = await apiFetch(`${API}/tax-drafts/modelo-130?year=${year}&quarter=${quarter}`);
      if (r130.ok) {
        setData130(await r130.json());
        setShow130(true);
      } else {
        // 403 = sociedad o sin estimación directa: el 130 no existe para este tenant
        setShow130(false);
        setData130(null);
      }
      const ri = await apiFetch(`${API}/tax-drafts/inputs?year=${year}&quarter=${quarter}`);
      if (ri.ok) setInputs(await ri.json());
    } catch {
      toast.error("No se pudieron cargar los borradores AEAT");
    } finally {
      setLoading(false);
    }
  }, [year, quarter, basis]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!request) return;
    setYear(request.year);
    setQuarter(request.quarter);
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [request]);

  const inputVal = (k) => (inputs[k] ?? "") === "" || inputs[k] == null ? "" : inputs[k];

  const saveInputs = async (fields) => {
    setSaving(true);
    try {
      const body = { year, quarter };
      for (const [k, v] of Object.entries(fields)) {
        body[k] = v === "" || v == null ? null : Number(v);
      }
      const r = await apiFetch(`${API}/tax-drafts/inputs`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail);
      }
      toast.success("Datos guardados; borrador recalculado");
      await load();
    } catch (e) {
      toast.error(e.message || "No se pudieron guardar los datos");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (modelo) => {
    try {
      const extra = modelo === "303" ? `&basis=${basis}` : "";
      const r = await apiFetch(
        `${API}/tax-drafts/modelo-${modelo}/pdf?year=${year}&quarter=${quarter}${extra}`
      );
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `borrador_${modelo}_${year}_T${quarter}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`No se pudo descargar el PDF del ${modelo}`);
    }
  };

  const resultado303 = data303?.casillas?.find((c) => c.num === "71");

  return (
    <div ref={sectionRef} id="borradores-aeat">
      <Card data-testid="tax-drafts-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Landmark className="w-5 h-5 text-slate-600" />
            Borradores AEAT (informativos) — 303 y 130
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 text-sm"
            data-testid="tax-drafts-disclaimer"
          >
            <strong>Borrador informativo — no válido para presentación.</strong>{" "}
            Tu gestoría presenta; esto te ayuda a anticipar. Cada casilla indica de
            dónde sale y su fuente oficial.
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="w-28">
              <Label className="mb-2 block">Año</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger data-testid="tax-drafts-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2].map((offset) => {
                    const y = now.getFullYear() - offset;
                    return (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="mb-2 block">Trimestre</Label>
              <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
                <SelectTrigger data-testid="tax-drafts-quarter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">T1 (Ene-Mar)</SelectItem>
                  <SelectItem value="2">T2 (Abr-Jun)</SelectItem>
                  <SelectItem value="3">T3 (Jul-Sep)</SelectItem>
                  <SelectItem value="4">T4 (Oct-Dic)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Facturas del 303</Label>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                <Button
                  variant={basis === "dates" ? "default" : "ghost"}
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setBasis("dates")}
                  title="Facturas con fecha dentro del trimestre"
                >
                  Por fechas
                </Button>
                <Button
                  variant={basis === "filed" ? "default" : "ghost"}
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setBasis("filed")}
                  title="Reconstruye el trimestre con lo sellado al dar por presentado (incluye adelantadas y excluye arrastradas)"
                  data-testid="tax-drafts-basis-filed"
                >
                  Según lo sellado
                </Button>
              </div>
            </div>
            {loading && <RefreshCw className="w-4 h-4 animate-spin text-slate-400 mb-2" />}
          </div>

          {err303 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-sm">
              {err303}
            </div>
          )}

          {data303 && (
            <div className="space-y-3" data-testid="tax-drafts-303">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-slate-900">
                  Modelo 303 · IVA · {data303.period.label}
                  {data303.period.basis === "filed" && (
                    <span className="ml-2 text-xs font-normal text-slate-500">
                      (según lo sellado)
                    </span>
                  )}
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  {resultado303 && (
                    <span className="text-sm text-slate-600">
                      Resultado:{" "}
                      <strong className="font-mono">{fmtEur(resultado303.importe)}</strong>
                    </span>
                  )}
                  <Button variant="outline" size="sm" onClick={() => downloadPdf("303")}
                          data-testid="tax-drafts-303-pdf">
                    <FileDown className="w-4 h-4 mr-1.5" />
                    PDF borrador
                  </Button>
                </div>
              </div>
              <CasillasTable casillas={data303.casillas} testid="tax-drafts-303-table" />
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-64">
                  <Label className="mb-1 block text-xs">
                    Cuotas a compensar de periodos anteriores (casilla 110)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={inputVal("iva_compensar_pendientes")}
                    onChange={(e) =>
                      setInputs((p) => ({ ...p, iva_compensar_pendientes: e.target.value }))
                    }
                    data-testid="tax-drafts-input-compensar"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  onClick={() =>
                    saveInputs({ iva_compensar_pendientes: inputs.iva_compensar_pendientes })
                  }
                >
                  Guardar y recalcular
                </Button>
                <p className="text-xs text-slate-500 basis-full">
                  La app no conoce tus autoliquidaciones anteriores: si tienes cuotas
                  pendientes de compensar, introdúcelas aquí.
                </p>
              </div>
              <NotasYFuentes data={data303} />
            </div>
          )}

          {show130 && data130 && (
            <div className="space-y-3 border-t border-slate-200 pt-4" data-testid="tax-drafts-130">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-slate-900">
                  Modelo 130 · Pago fraccionado IRPF · {data130.period.label}
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    (acumulado del año)
                  </span>
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-slate-600">
                    Resultado: <strong className="font-mono">{fmtEur(data130.resultado)}</strong>
                  </span>
                  <Button variant="outline" size="sm" onClick={() => downloadPdf("130")}
                          data-testid="tax-drafts-130-pdf">
                    <FileDown className="w-4 h-4 mr-1.5" />
                    PDF borrador
                  </Button>
                </div>
              </div>
              <CasillasTable casillas={data130.casillas} testid="tax-drafts-130-table" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs">
                    Pagos fraccionados anteriores del año (casilla 05)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`Propuesta de la app: ${fmtEur(
                      data130.inputs?.pagos_previos_propuesta || 0
                    )}`}
                    value={inputVal("irpf_pagos_previos")}
                    onChange={(e) =>
                      setInputs((p) => ({ ...p, irpf_pagos_previos: e.target.value }))
                    }
                    data-testid="tax-drafts-input-pagos-previos"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Vacío = usar la propuesta (recalculada de tus trimestres anteriores).
                    Escribe el importe real si presentaste otra cifra.
                  </p>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">
                    Minoración art. 110.3.c (casilla 13)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={inputVal("irpf_minoracion")}
                    onChange={(e) =>
                      setInputs((p) => ({ ...p, irpf_minoracion: e.target.value }))
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Solo si tu rendimiento neto del año pasado fue ≤ 12.000 €
                    (100/75/50/25 € por tramos).
                  </p>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">
                    Resultados negativos de trimestres anteriores (casilla 15)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`Propuesta: ${fmtEur(
                      data130.inputs?.negativos_previos_propuesta || 0
                    )}`}
                    value={inputVal("irpf_negativos_previos")}
                    onChange={(e) =>
                      setInputs((p) => ({ ...p, irpf_negativos_previos: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">
                    Deducción vivienda habitual (casilla 16)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={inputVal("irpf_vivienda")}
                    onChange={(e) =>
                      setInputs((p) => ({ ...p, irpf_vivienda: e.target.value }))
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Solo compras anteriores a 2013; se aplica con sus topes (2 % y 660,14 €).
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() =>
                  saveInputs({
                    irpf_pagos_previos: inputs.irpf_pagos_previos,
                    irpf_minoracion: inputs.irpf_minoracion,
                    irpf_negativos_previos: inputs.irpf_negativos_previos,
                    irpf_vivienda: inputs.irpf_vivienda,
                  })
                }
                data-testid="tax-drafts-130-save"
              >
                Guardar y recalcular
              </Button>
              <NotasYFuentes data={data130} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
